import type {
  PrivateAssetDto,
  PrivateAssetType,
} from "@/domains/documents/types";
import type { Asset, AssetType, PublicAsset } from "@/types";

/** Resolves the concrete asset shape for a given `type` discriminant. */
type AssetForType<T extends AssetType> = T extends PrivateAssetType
  ? PrivateAssetDto
  : PublicAsset;

export const getSortedAssetsByType = <T extends AssetType>(
  assets: Asset[],
  type: T,
): AssetForType<T>[] => {
  return assets
    .filter((asset) => asset.type === type)
    .sort((a, b) => a.sort_order - b.sort_order) as AssetForType<T>[];
};

export const getLastResumeAsset = (assets: Asset[]) => {
  return getSortedAssetsByType(assets, "RESUME").at(-1);
};

/** Returns the resume asset with is_default === true, or undefined if none. */
export const getDefaultResumeAsset = (assets: Asset[]) => {
  return getSortedAssetsByType(assets, "RESUME").find(
    (a) => a.is_default === true,
  );
};

export type TruncateFilenameOptions = {
  length?: number;
  ellipsis?: string;
  includeExtension?: boolean;
};

export function truncateFilename(
  name: string,
  options?: TruncateFilenameOptions,
): string {
  const {
    length = 30,
    ellipsis = "…",
    includeExtension = true,
  } = options ?? {};
  if (name.length <= length) return name;
  const ext = name.lastIndexOf(".");
  if (ext === -1) return `${name.slice(0, length)}${ellipsis}`;
  const extension = name.slice(ext);
  const maxBase = length - extension.length - 1;
  return `${name.slice(0, maxBase)}${ellipsis}${includeExtension ? extension : ""}`;
}
export const formatAssetFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatAssetDate = (date: Date | string) => {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.toLocaleDateString("it-IT", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};
